import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Camera, Trash2, RefreshCw, Image as ImageIcon, CheckCircle2, Circle, Download, X, Check, CloudOff, ZoomIn } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import Lightbox from '../components/Lightbox.jsx';
import { api } from '../services/api.js';
import { localDB } from '../services/db.js';
import { useStorage, useSyncQueue } from '../hooks/useStorage.js';
import { useFocusRefresh } from '../hooks/useFocusRefresh.js';
import { useNotificationRefresh } from '../hooks/useNotificationRefresh.js';
import { useSnapImage } from '../hooks/useImage.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { bytesToHuman, formatDate, cn } from '../lib/utils.js';

// Normalize any timestamp to milliseconds for reliable sorting
const toMs = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val < 1e12 ? val * 1000 : val;
  const t = new Date(val).getTime();
  return isNaN(t) ? 0 : t;
};

export default function Snaps() {
  const toast = useToast();
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [zoomed, setZoomed] = useState(null);
  const [selected, setSelected] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const { data: localSnaps, loading: localLoading, refresh: refreshLocal, saveItem, removeItem } = useStorage('snaps');
  const { queue, addToQueue } = useSyncQueue();

  const loadSnaps = useCallback(async () => {
    // 1. Always refresh local data immediately
    await refreshLocal();
    setError(null);

    // 2. Background sync from API if online
    if (navigator.onLine) {
      setSyncing(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);

      try {
        const res = await api.listSnaps('limit=60', controller.signal);
        clearTimeout(timeoutId);
        setApiData(res);

        // Update local cache and prune deleted items
        if (res?.data?.items) {
          const apiItems = res.data.items;
          const apiIds = new Set(apiItems.map(s => s.id));
          
          // Get all current local snaps to check for deletions
          const currentLocal = await localDB.getAll('snaps');
          for (const snap of currentLocal) {
            // If it's a server-synced snap (no 'pending' flag) but not in the API list, it was deleted
            if (!snap.pending && !apiIds.has(snap.id)) {
              await localDB.delete('snaps', snap.id);
            }
          }

          // Save/Update new items
          for (const snap of apiItems) {
            await localDB.put('snaps', snap);
          }
          
          // Refresh local state after pruning
          await refreshLocal();
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.warn('[Snaps] Background fetch timed out');
        } else {
          console.warn('[Snaps] Background fetch failed:', err);
        }
      } finally {
        setSyncing(false);
      }
    }
  }, [refreshLocal]);

  useEffect(() => {
    loadSnaps();
  }, [loadSnaps]);

  useFocusRefresh(loadSnaps);
  useNotificationRefresh(loadSnaps, 'syncvault:refresh:snaps');

  // Merge and deduplicate snaps
  const items = useMemo(() => {
    // If we have API data, it's our source of truth for synced items
    const apiItems = apiData?.data?.items || [];
    const apiIds = new Set(apiItems.map(s => s.id));
    
    // Local items are:
    // 1. Pending uploads (temp- IDs)
    // 2. Cached items NOT yet in apiData (if apiData hasn't loaded or is partial)
    const localItems = (localSnaps || []).filter(s => {
      if (apiIds.has(s.id)) return false;
      // If we have API data, we only trust local items if they are pending uploads
      if (apiData) return s.pending;
      // If offline/no API data, show all local cache
      return true;
    });
    
    return [...localItems, ...apiItems].sort((a, b) => 
      toMs(b.created_at) - toMs(a.created_at)
    );
  }, [apiData, localSnaps]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const tempId = `temp-${Date.now()}`;
    const localSnap = {
      id: tempId,
      filename: file.name,
      caption: caption,
      size_bytes: file.size,
      created_at: new Date().toISOString(),
      blob: file, // Store the blob for local viewing
      pending: true
    };

    try {
      // 1. Save locally first
      await saveItem(localSnap);
      setCaption('');

      // 2. Try API
      const fd = new FormData();
      fd.append('file', file);
      if (caption) fd.append('caption', caption);
      
      const res = await api.uploadSnap(fd);
      
      // 3. Clean up local temp and save real metadata
      await removeItem(tempId);
      toast.success('Snap uploaded');
      loadSnaps();
    } catch (err) {
      console.warn('[Snaps] Upload failed, queuing for sync:', err);
      await addToQueue({ 
        type: 'CREATE_SNAP', 
        data: { 
          caption: localSnap.caption, 
          tempId: localSnap.id, 
          blob: file 
        } 
      });
      toast.info('Saved locally (offline)');
    } finally {
      setBusy(true); // Keep busy until reload or state settles
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this snap?')) return;
    try {
      if (String(id).startsWith('temp-')) {
        await removeItem(id);
      } else {
        await api.deleteSnap(id);
      }
      toast.info('Snap removed');
      loadSnaps();
    } catch (err) { 
      toast.error(err.message); 
      if (!String(id).startsWith('temp-')) {
        await addToQueue({ type: 'DELETE_SNAP', id });
        toast.info('Marked for deletion (offline)');
      }
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const clearSelection = () => {
    setSelected([]);
    setIsSelecting(false);
  };

  const bulkDelete = async () => {
    if (!selected.length || !confirm(`Delete ${selected.length} snaps?`)) return;
    setBusy(true);
    try {
      await Promise.all(selected.map(id => api.deleteSnap(id)));
      toast.info(`${selected.length} snaps removed`);
      clearSelection();
      loadSnaps();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const bulkDownload = () => {
    selected.forEach(id => {
      const url = api.snapFileUrl(id);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snap-${id}`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
  };

  const handleSelectButtonClick = () => {
    if (isSelecting || selected.length > 0) {
      clearSelection();
    } else {
      setIsSelecting(true);
    }
  };

  const renderActions = () => {
    if (selected.length > 0 || isSelecting) {
      return (
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <span className="mr-2 text-xs font-medium text-accent">
              {selected.length} selected
            </span>
          )}
          {selected.length > 0 && (
            <>
              <button className="btn-ghost !px-2" onClick={bulkDownload} title="Download selected">
                <Download size={14} /> <span className="text-xs sm:inline sm:text-sm">Download</span>
              </button>
              <button className="btn-danger !px-2" onClick={bulkDelete} title="Delete selected">
                <Trash2 size={14} /> <span className="text-xs sm:inline sm:text-sm">Delete</span>
              </button>
            </>
          )}
          <button className="btn-ghost !px-2" onClick={clearSelection}>
            <X size={14} /> <span className="text-xs sm:inline sm:text-sm">Cancel</span>
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <label className="btn-ghost cursor-pointer !px-2 sm:!px-3">
          <ImageIcon size={14} /> <span className="text-xs sm:text-sm">Upload</span>
          <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <label className="btn-primary cursor-pointer !px-2 sm:!px-3">
          <Camera size={14} /> <span className="text-xs sm:text-sm">Capture</span>
          <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <button 
          className={`btn-ghost !px-2 sm:!px-3 ${isSelecting ? 'bg-accent/20 text-accent' : ''}`} 
          onClick={handleSelectButtonClick}
        >
          <Check size={14} /> <span className="text-xs sm:text-sm">Select</span>
        </button>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Snapshots"
        subtitle="Photos you have captured, available on every device"
        actions={renderActions()}
      />

      <div className="card mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="label">Caption (optional)</label>
          <input className="input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="What's in this snap?" />
        </div>
        <button className="btn-ghost self-stretch sm:self-end flex items-center gap-2" onClick={loadSnaps}>
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Refresh'}
        </button>
      </div>

      {localLoading && syncing && items.length === 0 && <div className="card text-sm text-zinc-400">Loading snaps...</div>}
      {!localLoading && error && items.length === 0 && (
        <div className="card text-sm text-zinc-400">
          <div className="flex items-center gap-2">
            <CloudOff size={16} />
            <span>No snapshots available offline</span>
          </div>
          <p className="mt-1 text-xs text-zinc-500">Connect to the internet to load your snaps.</p>
        </div>
      )}
      {!localLoading && !error && items.length === 0 && (
        <EmptyState icon={Camera} title="No snapshots yet" description="Use the Capture button to take a photo or upload an existing image." />
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((s) => (
          <SnapCard 
            key={s.id} 
            snap={s} 
            onDelete={() => remove(s.id)} 
            onZoom={() => setZoomed(s)}
            isSelected={selected.includes(s.id)}
            isSelecting={isSelecting || selected.length > 0}
            onSelect={() => toggleSelect(s.id)}
          />
        ))}
      </div>

      {zoomed && <Lightbox snap={zoomed} onClose={() => setZoomed(null)} />}
    </div>
  );
}

function SnapCard({ snap, onDelete, onZoom, isSelected, isSelecting, onSelect }) {
  const src = useSnapImage(snap.id);
  const timerRef = useRef(null);

  const handleTouchStart = () => {
    timerRef.current = setTimeout(() => {
      onSelect();
    }, 500);
  };

  const handleTouchEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const handleClick = (e) => {
    if (isSelecting) {
      onSelect();
    } else {
      onZoom();
    }
  };

  return (
    <div className={cn(
      'card overflow-hidden p-0 transition-all duration-200 group',
      isSelected && 'ring-2 ring-accent',
      snap.pending && 'opacity-70 border-amber-500/30'
    )}>
      <button
        type="button"
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onContextMenu={(e) => { e.preventDefault(); onSelect(); }}
        className="relative block aspect-square w-full bg-zinc-900 overflow-hidden"
        title={snap.caption || snap.filename}
      >
        {/* Image or skeleton shimmer */}
        {src
          ? <img src={src} alt={snap.filename} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" loading="lazy" />
          : <div className="skeleton h-full w-full" />}

        {/* Hover overlay with actions */}
        {!isSelecting && src && (
          <div className="absolute inset-0 flex items-center justify-center gap-3 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="rounded-full bg-white/10 p-2 backdrop-blur-sm border border-white/10">
              <ZoomIn size={16} className="text-white" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="rounded-full bg-rose-500/80 p-2 backdrop-blur-sm border border-rose-400/20 hover:bg-rose-500 transition-colors"
              title="Delete"
            >
              <Trash2 size={16} className="text-white" />
            </button>
          </div>
        )}

        {/* Select checkmark */}
        {(isSelecting || isSelected) && (
          <div className="absolute right-2 top-2 rounded-full bg-black/50 p-1 backdrop-blur-sm">
            {isSelected ? (
              <CheckCircle2 size={18} className="text-accent-light" />
            ) : (
              <Circle size={18} className="text-zinc-400" />
            )}
          </div>
        )}

        {/* Pending upload badge */}
        {snap.pending && (
          <div className="absolute left-2 top-2 rounded-full bg-amber-500/20 p-1 text-amber-500 backdrop-blur-sm" title="Pending upload">
            <CloudOff size={14} />
          </div>
        )}
      </button>

      {/* Card footer — only shown when not in select mode and there's a caption */}
      {snap.caption && (
        <div className="px-2 py-1.5">
          <div className="truncate text-xs text-zinc-300" title={snap.caption}>{snap.caption}</div>
        </div>
      )}

      {/* Slim date/size row — always show */}
      <div className="flex items-center justify-between px-2 pb-1.5 text-[10px] text-zinc-600">
        <span>{formatDate(snap.created_at)}</span>
        <span>{bytesToHuman(snap.size_bytes)}</span>
      </div>
    </div>
  );
}

